/**
 * Permission utility functions for checking user access
 */

/**
 * Check if user has a specific permission
 * @param {Object} user - The user object from AuthContext
 * @param {string} permission - The permission to check (e.g., 'view animals')
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  
  // Super admins have all permissions
  if (user.is_super_admin) return true;
  
  // Check if permission exists in user's permissions array
  return user.permissions?.includes(permission) || false;
};

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - The user object
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user) return false;
  if (user.is_super_admin) return true;
  
  return permissions.some(permission => user.permissions?.includes(permission));
};

/**
 * Check if user has all of the specified permissions
 * @param {Object} user - The user object
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user) return false;
  if (user.is_super_admin) return true;
  
  return permissions.every(permission => user.permissions?.includes(permission));
};

/**
 * Check if user has a specific role
 * @param {Object} user - The user object
 * @param {string} role - The role name to check
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  if (!user) return false;
  return user.roles?.includes(role) || false;
};

/**
 * Check if user has any of the specified roles
 * @param {Object} user - The user object
 * @param {string[]} roles - Array of role names to check
 * @returns {boolean}
 */
export const hasAnyRole = (user, roles) => {
  if (!user) return false;
  return roles.some(role => user.roles?.includes(role));
};

/**
 * Check if user is super admin
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const isSuperAdmin = (user) => {
  return user?.is_super_admin === true;
};

/**
 * Check if user is admin of any farm
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const isFarmAdmin = (user) => {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return user.farms?.some(farm => farm.is_farm_admin) || false;
};

/**
 * Check if user has access to a specific farm
 * @param {Object} user - The user object
 * @param {number} farmId - The farm ID to check
 * @returns {boolean}
 */
export const hasAccessToFarm = (user, farmId) => {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return user.farms?.some(farm => farm.id === farmId) || false;
};

/**
 * Get IDs of farms user has access to
 * @param {Object} user - The user object
 * @returns {number[]}
 */
export const getAccessibleFarmIds = (user) => {
  if (!user) return [];
  return user.farms?.map(farm => farm.id) || [];
};

/**
 * Permission constants - matches backend permissions
 */
export const PERMISSIONS = {
  // Dashboard
  VIEW_DASHBOARD: 'view dashboard',
  
  // Farms
  VIEW_FARMS: 'view farms',
  CREATE_FARMS: 'create farms',
  EDIT_FARMS: 'edit farms',
  DELETE_FARMS: 'delete farms',
  MANAGE_FARM_USERS: 'manage farm users',
  
  // Animals - Core
  VIEW_ANIMALS: 'view animals',
  CREATE_ANIMALS: 'create animals',
  EDIT_ANIMALS: 'edit animals',
  DELETE_ANIMALS: 'delete animals',
  
  // Animal Groups
  VIEW_ANIMAL_GROUPS: 'view animal groups',
  CREATE_ANIMAL_GROUPS: 'create animal groups',
  EDIT_ANIMAL_GROUPS: 'edit animal groups',
  DELETE_ANIMAL_GROUPS: 'delete animal groups',
  
  // Animal Details
  VIEW_ANIMAL_DETAILS: 'view animal details',
  EDIT_ANIMAL_DETAILS: 'edit animal details',
  
  // Animal Notes
  VIEW_ANIMAL_NOTES: 'view animal notes',
  CREATE_ANIMAL_NOTES: 'create animal notes',
  EDIT_ANIMAL_NOTES: 'edit animal notes',
  DELETE_ANIMAL_NOTES: 'delete animal notes',
  VIEW_ANIMAL_OBSERVATIONS: 'view animal observations',
  CREATE_ANIMAL_OBSERVATIONS: 'create animal observations',
  EDIT_ANIMAL_OBSERVATIONS: 'edit animal observations',
  DELETE_ANIMAL_OBSERVATIONS: 'delete animal observations',
  
  // Animal Schedule & Tasks
  VIEW_ANIMAL_SCHEDULE: 'view animal schedule',
  CREATE_ANIMAL_SCHEDULE: 'create animal schedule',
  EDIT_ANIMAL_SCHEDULE: 'edit animal schedule',
  DELETE_ANIMAL_SCHEDULE: 'delete animal schedule',
  VIEW_ANIMAL_TASKS: 'view animal tasks',
  CREATE_ANIMAL_TASKS: 'create animal tasks',
  EDIT_ANIMAL_TASKS: 'edit animal tasks',
  DELETE_ANIMAL_TASKS: 'delete animal tasks',
  
  // Animal Health & Treatments
  VIEW_ANIMAL_HEALTH: 'view animal health',
  MANAGE_ANIMAL_HEALTH: 'manage animal health',
  VIEW_ANIMAL_TREATMENTS: 'view animal treatments',
  CREATE_ANIMAL_TREATMENTS: 'create animal treatments',
  EDIT_ANIMAL_TREATMENTS: 'edit animal treatments',
  DELETE_ANIMAL_TREATMENTS: 'delete animal treatments',
  
  // Animal Feedings & Inputs
  VIEW_ANIMAL_FEEDINGS: 'view animal feedings',
  CREATE_ANIMAL_FEEDINGS: 'create animal feedings',
  EDIT_ANIMAL_FEEDINGS: 'edit animal feedings',
  DELETE_ANIMAL_FEEDINGS: 'delete animal feedings',
  VIEW_ANIMAL_INPUTS: 'view animal inputs',
  CREATE_ANIMAL_INPUTS: 'create animal inputs',
  EDIT_ANIMAL_INPUTS: 'edit animal inputs',
  DELETE_ANIMAL_INPUTS: 'delete animal inputs',
  
  // Animal Measurements
  VIEW_ANIMAL_MEASUREMENTS: 'view animal measurements',
  CREATE_ANIMAL_MEASUREMENTS: 'create animal measurements',
  EDIT_ANIMAL_MEASUREMENTS: 'edit animal measurements',
  DELETE_ANIMAL_MEASUREMENTS: 'delete animal measurements',
  
  // Animal Breeding
  VIEW_ANIMAL_BREEDING: 'view animal breeding',
  CREATE_ANIMAL_BREEDING: 'create animal breeding',
  EDIT_ANIMAL_BREEDING: 'edit animal breeding',
  DELETE_ANIMAL_BREEDING: 'delete animal breeding',
  
  // Animal Offspring
  VIEW_ANIMAL_OFFSPRING: 'view animal offspring',
  CREATE_ANIMAL_OFFSPRING: 'create animal offspring',
  EDIT_ANIMAL_OFFSPRING: 'edit animal offspring',
  DELETE_ANIMAL_OFFSPRING: 'delete animal offspring',
  
  // Animal Siblings
  VIEW_ANIMAL_SIBLINGS: 'view animal siblings',
  
  // Animal Genealogy
  VIEW_ANIMAL_GENEALOGY: 'view animal genealogy',
  EDIT_ANIMAL_GENEALOGY: 'edit animal genealogy',
  
  // Animal Production
  VIEW_ANIMAL_PRODUCTION: 'view animal production',
  CREATE_ANIMAL_PRODUCTION: 'create animal production',
  EDIT_ANIMAL_PRODUCTION: 'edit animal production',
  DELETE_ANIMAL_PRODUCTION: 'delete animal production',
  VIEW_ANIMAL_YIELDS: 'view animal yields',
  CREATE_ANIMAL_YIELDS: 'create animal yields',
  EDIT_ANIMAL_YIELDS: 'edit animal yields',
  DELETE_ANIMAL_YIELDS: 'delete animal yields',
  
  // Animal Grazing
  VIEW_ANIMAL_GRAZING: 'view animal grazing',
  CREATE_ANIMAL_GRAZING: 'create animal grazing',
  EDIT_ANIMAL_GRAZING: 'edit animal grazing',
  DELETE_ANIMAL_GRAZING: 'delete animal grazing',
  
  // Animal Accounting
  VIEW_ANIMAL_ACCOUNTING: 'view animal accounting',
  CREATE_ANIMAL_ACCOUNTING: 'create animal accounting',
  EDIT_ANIMAL_ACCOUNTING: 'edit animal accounting',
  DELETE_ANIMAL_ACCOUNTING: 'delete animal accounting',
  
  // Animal Movements
  VIEW_ANIMAL_MOVEMENTS: 'view animal movements',
  CREATE_ANIMAL_MOVEMENTS: 'create animal movements',
  DELETE_ANIMAL_MOVEMENTS: 'delete animal movements',
  
  // Animal Photos & Files
  VIEW_ANIMAL_FILES: 'view animal files',
  UPLOAD_ANIMAL_FILES: 'upload animal files',
  DELETE_ANIMAL_FILES: 'delete animal files',
  
  
  // Contacts
  VIEW_CONTACTS: 'view contacts',
  CREATE_CONTACTS: 'create contacts',
  EDIT_CONTACTS: 'edit contacts',
  DELETE_CONTACTS: 'delete contacts',
  
  // Financial
  VIEW_EXPENSES: 'view expenses',
  CREATE_EXPENSES: 'create expenses',
  EDIT_EXPENSES: 'edit expenses',
  DELETE_EXPENSES: 'delete expenses',
  VIEW_REVENUES: 'view revenues',
  CREATE_REVENUES: 'create revenues',
  EDIT_REVENUES: 'edit revenues',
  DELETE_REVENUES: 'delete revenues',
  VIEW_TRANSACTIONS: 'view transactions',
  CREATE_TRANSACTIONS: 'create transactions',
  EDIT_TRANSACTIONS: 'edit transactions',
  DELETE_TRANSACTIONS: 'delete transactions',
  
  // Reports
  VIEW_REPORTS: 'view reports',
  EXPORT_REPORTS: 'export reports',
  
  // Users & Roles
  VIEW_USERS: 'view users',
  CREATE_USERS: 'create users',
  EDIT_USERS: 'edit users',
  DELETE_USERS: 'delete users',
  MANAGE_ROLES: 'manage roles',
};

/**
 * Default roles list
 */
export const DEFAULT_ROLES = [
  'Admin',
  'Operations Manager',
  'Farmhand',
  'Accountant',
  'Shop Manager',
  'Shop Worker',
  'Service Manager',
  'Warehouse Manager',
  'Laborer',
  'Agronomist',
  'Veterinarian',
  'Auditor',
  'Read Only',
];

